import { Trans, useTranslation } from 'react-i18next';

import { Anchor, Body1, Modal } from '@myphx-engineering/phx-design-system';
import { constants as dsConstants } from '@myphx-engineering/phx-design-system/framework';
import { useDisclosure } from '@myphx-engineering/phx-design-system/hooks';

import configs from '../../../configuration/lilly-direct';
import {
    type DigitallyReceivedPrescriberOrderInfoFragment,
    PrescriberOrderStatus,
} from '../../../graphql/generated/graphql';
import { useFlags } from '../../../hooks/use-flags';
import type { NdcDefinition } from '../../../types/drug-config';
import type { PricingInfo } from '../../../util/config/get-pricing';
import { ConditionalComponent } from '../../utility/ConditionalComponent';

import MedicationPricing from './MedicationPricing';
import { SwitchToCashLink } from './SwitchToCashLink';
import { ViewSwitchOptions } from './ViewSwitchOptions';

type PreviewInsuranceDetailsProps = {
    ndcDefinition: NdcDefinition;
    prescriberOrder: DigitallyReceivedPrescriberOrderInfoFragment;
    pricingInfo: PricingInfo;
};

export const PreviewInsuranceDetails = ({
    ndcDefinition,
    prescriberOrder,
    pricingInfo,
}: PreviewInsuranceDetailsProps) => {
    const { t } = useTranslation();
    const [opened, { toggle }] = useDisclosure(false);

    const { enableUpdatedDrugSwitchLilly } = useFlags();
    const isPrescriptionOnHold =
        prescriberOrder.status === PrescriberOrderStatus.OnHold;

    return (
        <>
            <Body1>
                <Trans
                    i18nKey="previewDetails.insuranceDescription"
                    values={{ reg: dsConstants.unicode.REGISTERED }}
                    components={{
                        1: (
                            <Anchor
                                textAlign="left"
                                onClick={toggle}
                                textDecoration="underline"
                            />
                        ),
                    }}
                />
            </Body1>

            <Modal
                title={t('previewDetails.termsAndConditions.pen.heading', {
                    reg: dsConstants.unicode.REGISTERED,
                })}
                opened={opened}
                onClose={toggle}
            >
                <Trans
                    i18nKey="previewDetails.termsAndConditions.pen.body"
                    values={{ reg: dsConstants.unicode.REGISTERED }}
                    parent="div"
                    data-testid="pharmacy-preview-terms-and-conditions"
                    components={{
                        1: (
                            <Anchor
                                target="_blank"
                                href={configs.constants.LILLY_SAFETY_URL}
                                textDecoration="underline"
                            />
                        ),
                    }}
                />
            </Modal>

            <MedicationPricing
                pricingInfo={pricingInfo}
                data-testid="pharmacy-preview-pricing-card"
            />

            <ConditionalComponent condition={!isPrescriptionOnHold}>
                {enableUpdatedDrugSwitchLilly ? (
                    <ViewSwitchOptions
                        ndcDefinition={ndcDefinition}
                        order={prescriberOrder}
                        position="description"
                    />
                ) : (
                    <SwitchToCashLink
                        ndcDefinition={ndcDefinition}
                        order={prescriberOrder}
                    />
                )}
            </ConditionalComponent>
        </>
    );
};
